import { Route, Routes } from "@angular/router";

/** Shared config for the /career routes — all render the embed host component. */
const careersRoute: Omit<Route, "path"> = {
  loadComponent: () =>
    import("./pages/careers/careers.component").then((m) => m.CareersComponent),
  title: "Relief Applications - Join our talented team",
  data: { seo: {
    title: 'Relief Applications – Join Our Talented Team',
    description: 'Join our talented team at Relief Applications. Explore open positions and help bring technology solutions to humanitarian organizations.',
    image: '/assets/images/logos/reliefapps-sharing-web.jpg',
  }},
};

export const routes: Routes = [
  { path: "", redirectTo: "home", pathMatch: "full" },
  {
    path: "home",
    loadComponent: () =>
      import("./pages/home/home.component").then((m) => m.HomeComponent),
    title: "Relief Applications - Bringing Solutions to those Changing the World",
    data: { seo: {
      title: 'Relief Applications – Technology for Nonprofits',
      description: 'Experts in data management and collection solutions and app development for nonprofit organizations.',
      image: '/assets/images/logos/reliefapps-sharing-web.jpg',
    }}
  },
  {
    path: "services",
    loadComponent: () =>
      import("./pages/consulting/consulting.component").then(
        (m) => m.ConsultingComponent
      ),
    title: "Relief Applications - Services we offer",
    data: { seo: {
      title: 'Relief Applications – Services We Offer',
      description: 'Expert humanitarian technology consulting: information management, capacity building, MEAL, app development, AI tools, and more.',
      image: '/assets/images/pages/consulting/header.png',
    }}
  },
  {
    path: "services/:service",
    loadComponent: () =>
      import("./pages/consulting/consulting.component").then(
        (m) => m.ConsultingComponent
      ),
    title: "Relief Applications - Services we offer"
  },
  {
    path: "relief-apps",
    loadComponent: () =>
      import("./pages/relief-apps/relief-apps.component").then(
        (m) => m.ReliefAppsComponent
      ),
    title: "Relief Applications - Toolbox & custom apps",
    data: { seo: {
      title: 'Relief Applications – Toolbox & Custom Apps',
      description: 'Ready-to-deploy apps for nonprofits: warehouse management, case management, M&E systems, and custom solutions.',
      image: '/assets/images/pages/relief-apps/customApp.webp',
    }}
  },
  {
    path: "projects",
    loadComponent: () =>
      import("./pages/projects/projects.component").then(
        (m) => m.ProjectsComponent
      ),
    title: "Relief Applications - Our portfolio",
    data: { seo: {
      title: 'Relief Applications – Our Portfolio',
      description: 'Explore our portfolio of humanitarian technology projects for NGOs and nonprofits worldwide.',
      image: '/assets/images/logos/reliefapps-sharing-web.jpg',
    }}
  },
  {
    path: "projects/:id",
    loadComponent: () =>
      import("./pages/project/project.component").then(
        (m) => m.ProjectComponent
      ),
  },
  {
    path: "suites/:id",
    loadComponent: () =>
      import("./pages/suite/suite.component").then((m) => m.SuiteComponent),
  },
  {
    path: "about",
    loadComponent: () =>
      import("./pages/about/about.component").then((m) => m.AboutComponent),
    title: "Relief Applications - Know more about us",
    data: { seo: {
      title: 'Relief Applications – Know More About Us',
      description: 'Learn about Relief Applications — our mission, team, and commitment to humanitarian innovation.',
      image: '/assets/images/logos/reliefapps-sharing-web.jpg',
    }}
  },
  {
    path: "pricing",
    loadComponent: () =>
      import("./pages/pricing/pricing.component").then(
        (m) => m.PricingComponent
      ),
    title: "Relief Applications - Pricing",
    data: { seo: {
      title: 'Relief Applications – Pricing',
      description: 'Transparent pricing for our suite of nonprofit technology apps and consulting services.',
      image: '/assets/images/logos/reliefapps-sharing-web.jpg',
    }}
  },
  {
    path: "partners",
    loadComponent: () =>
      import("./pages/partners/partners.component").then(
        (m) => m.PartnersComponent
      ),
    title: "Relief Applications - Partners we work with",
    data: { seo: {
      title: 'Relief Applications – Partners We Work With',
      description: 'Our trusted partners in delivering humanitarian technology solutions globally.',
      image: '/assets/images/logos/reliefapps-sharing-web.jpg',
    }}
  },
  {
    path: "terms-of-services",
    loadComponent: () =>
      import(
        "./pages/other/terms-of-services/terms-of-services.component"
      ).then((m) => m.TermsOfServicesComponent),
    title: "Relief Applications - Terms of services",
    data: { seo: {
      title: 'Relief Applications – Terms of Services',
      description: 'Relief Applications terms of services.',
      image: '/assets/images/logos/reliefapps-sharing-web.jpg',
    }}
  },
  {
    path: "privacy-policy",
    loadComponent: () =>
      import("./pages/other/privacy-policy/privacy-policy.component").then(
        (m) => m.PrivacyPolicyComponent
      ),
    title: "Relief Applications - Privacy Policy",
    data: { seo: {
      title: 'Relief Applications – Privacy Policy',
      description: 'Relief Applications privacy policy.',
      image: '/assets/images/logos/reliefapps-sharing-web.jpg',
    }}
  },
  {
    path: "commitment",
    loadComponent: () =>
      import("./pages/commitment/commitment.component").then(
        (m) => m.CommitmentComponent
      ),
    title: "Relief Applications - Our principles and commitments",
    data: { seo: {
      title: 'Relief Applications – Our Principles and Commitments',
      description: 'Our principles and commitments: Expertise, Dedication, Empathy, and Gratitude.',
      image: '/assets/images/logos/reliefapps-sharing-web.jpg',
    }}
  },
  // All three point at the embed host. `career/application/:token` lets a shared
  // "Follow your application" link survive a hard reload; `career/:slug` does the same
  // for a single role. The component builds the matching /careers/embed sub-path.
  { path: 'career', ...careersRoute },
  { path: 'career/application/:token', ...careersRoute },
  { path: 'career/:slug', ...careersRoute },
  {
    path: "custom",
    loadComponent: () =>
      import("./pages/custom/custom.component").then(
        (m) => m.CustomComponent
      ),
    title: "Relief Applications - We develop your own custom solution",
    data: { seo: {
      title: 'Relief Applications – Custom Solution Development',
      description: 'We design and build custom technology solutions for nonprofit organizations worldwide.',
      image: '/assets/images/pages/custom/customAppImage.png',
    }}
  },
  {
    path: "dp4g",
    loadComponent: () =>
      import("./pages/dp4g/dp4g.component").then(
        (m) => m.Dp4gComponent
      ),
    data: { lang: "en", seo: {
      title: 'DP4G – Data Protection for Good',
      description: 'DP4G: Dedicated data protection expertise for humanitarian organizations. GDPR knowledge paired with humanitarian frameworks.',
      image: '/assets/images/pages/dp4g/header.png',
    }},
    title: "Relief Applications - Data protection for good"
  },
  {
    path: "dp4g/fr",
    loadComponent: () =>
      import("./pages/dp4g/dp4g.component").then(
        (m) => m.Dp4gComponent
      ),
    data: { lang: "fr", seo: {
      title: 'DP4G – Protection des Données pour le Bien Commun',
      description: 'DP4G : Expertise en protection des données pour les organisations humanitaires. Connaissance RGPD adaptée aux cadres humanitaires.',
      image: '/assets/images/pages/dp4g/header.png',
    }},
    title: "Relief Applications - Protection des données pour le bien commun"
  },

  // Handle old website routes
  { path: "project", redirectTo: "projects" },
  { path: "project/:id", redirectTo: "projects/:id" },
  { path: "terms-of-use", redirectTo: "terms-of-services" },
  // TODO -> Check previous site routes and redirect them to the relevant page

  {
    path: "**",
    redirectTo: "home",
    title: "Relief Applications - Bringing Solutions to those Changing the World"
  },
];
